import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactElement } from "react";
import type { ApiClient, Artikel, ArtikelKatalog, KategorieZeile, Unternehmen } from "@kundeninfo/api-client";
import { ApiError } from "@kundeninfo/api-client";
import { Icon, useTelefon } from "@kundeninfo/ui";
import { useBestaetigung } from "@kundeninfo/ui";
import { Knopf, Menue, Pfad, Pillenreihe, Stufe } from "@kundeninfo/ui/me-design";
import type { MenueElement } from "@kundeninfo/ui/me-design";
import { UnternehmensChips, sichtFuer } from "../components/Filterkopf.js";
import { Suchfeld } from "../components/meHuellen.js";
import { useVerzoegerteSuche } from "../components/verzoegerteSuche.js";
import { useStabileListe } from "../components/stabileListe.js";
import { ArtikelDialog } from "../components/artikel/ArtikelDialog.js";
import type { ArtikelDialogClient } from "../components/artikel/ArtikelDialog.js";
import { KategorienPflege } from "../components/artikel/KategorienPflege.js";
import type { KategorienPflegeClient } from "../components/artikel/KategorienPflege.js";
import { ARTICLE_DIALOG_TEXT as DT } from "../components/artikel/articleDialogTexts.js";
import { ArtikelKachel } from "./artikel/ArtikelKachel.js";
import { KategorienListe, OHNE_KATEGORIE, kategorieName, sichtbareZeilen, summe } from "./artikel/KategorienListe.js";
import { Spaltenwahl } from "./artikel/Spaltenwahl.js";
import type { ProduktSpalten } from "./artikel/Spaltenwahl.js";
import { ARTICLE_TEXT as T, articleCount, groupHead, searchEmpty } from "./artikel/articleTexts.js";
import "./BereichArtikel.css";

export type { ProduktSpalten } from "./artikel/Spaltenwahl.js";

/**
 * Der Bereich „Artikel“ nach dem Artikelmodul (Spec 2026-11-04 0.4, „Oberfläche › Aufbau“; Entscheide 792–802):
 * links die KATEGORIEN des Arbeitsunternehmens (Zähler, Preisspanne, Warnstatus), rechts Pfad, Werkzeugzeile mit
 * Suche, Unternehmens-Chips, Pille „stillgelegte n“ und Spaltenwahl (798), darunter das KACHELRASTER nach Variante A
 * (802) mit Preis und Einheit im Kopf (797).
 *
 * **Ein Hook, zwei Stücke.** Wie die Schwesterbereiche füllt er `MeSchale.list` (`spalte`) und `MeSchale.detail`
 * (`detail`) aus EINEM Zustand. Am Telefon (`TELEFON_ABFRAGE`) gibt es keine Spalte (`spalte === null`): Die Kategorien
 * stehen dann als Pillenreihe oben im Detail, das Raster ist einspaltig, die Spaltenwahl entfällt (der gespeicherte Wert
 * bleibt).
 *
 * **Laden (792).** `GET /api/artikel/katalog` liefert ALLE Artikel des Filters (Unternehmen, Pille, Suche) samt den
 * verdichteten Kategorienzeilen in EINER Antwort und in fester Ordnung (796) — die Oberfläche sortiert nie nach. Der
 * Kategoriefilter wirkt auf die geladene Menge, ohne neue Anfrage; die Spaltenwahl setzt nur `--n`. Beim Neuladen bleibt
 * die alte Menge stehen, bis die neue da ist: kein „Lädt …“ über einem Raster, das schon steht, kein Scrollsprung.
 *
 * **Adresse `/artikel/:id` (793, RF5).** Der Artikel wird im geladenen Katalog gesucht: gefunden → seine Kategorie
 * gewählt, die Kachel markiert und mit `scrollIntoView({block: "nearest"})` in Sicht. Fehlt er bei ausgeschalteter
 * Pille, schaltet sie sich zu (er könnte stillgelegt sein); fehlt er auch dann, stehen „Alle“ und die Quittung „Diesen
 * Artikel gibt es nicht …“, und die selbst zugeschaltete Pille geht wieder aus.
 *
 * **Pflege (788, nur ADMIN).** Der Stift an der Kachel öffnet den Artikel-Dialog mit dem Artikel (auch an Kacheln
 * zugeschalteter Unternehmen, dann mit deren Vorgaben und Chip), „Neuer Artikel“ ihn leer im Arbeitsunternehmen mit
 * der gewählten Kategorie (P17). Das ⋯ bietet „Stilllegen“ bzw. „Wieder aktivieren“ mit gestapelter Rückfrage; eine
 * 409 (RF4) sagt es und lädt neu. Der Stift im Kopf der Kategorien öffnet die Kategorienpflege. Nach jedem Schreiben
 * lädt der Katalog mit unveränderten Filtern neu, die Scrollposition bleibt, und die geschriebene Kachel kommt mit
 * `scrollIntoView({block: "nearest"})` in Sicht.
 *
 * **Adresse beim Wechsel (M6).** Wählt die Nutzerin eine andere Kategorie, Sicht, Pille oder Suche, während die Adresse
 * `/artikel/:id` trägt, geht sie zurück auf `/artikel` — die Adresse nennt dann nicht mehr, was zu sehen ist.
 */

/** Was der Bereich vom Klienten braucht: Katalog, Artikel-Dialog, Stilllegen und Kategorienpflege. */
export type BereichArtikelClient = Pick<ApiClient, "getProductCatalog" | "setProductActive"> & ArtikelDialogClient
  & KategorienPflegeClient;

export type BereichArtikelProps = {
  /** Steht der Bereich gerade auf dem Bildschirm? Ohne diesen Riegel liefe der Abruf an jeder Adresse mit. */
  aktiv: boolean;
  client: BereichArtikelClient;
  /** Die sichtbaren Konzernunternehmen: Unternehmens-Chips und das Kennzeichen an der Kachel. */
  companies: Unternehmen[];
  /** Das Arbeitsunternehmen aus der Kopfleiste (R21): der feste Chip, die übrigen schalten sich dazu. */
  arbeitsunternehmenId: string | null;
  /** Der Artikel aus der Adresse `/artikel/:id` (793) — `null` heißt „keiner“. */
  artikelId: string | null;
  /** Stift, ⋯, „Neuer Artikel“ und Kategorienpflege nur für ADMIN (789); der Server prüft es ohnehin. */
  istAdmin: boolean;
  /** Die Spaltenzahl der Kachelansicht aus der Sitzung (798). */
  spalten: ProduktSpalten;
  /** Die Wahl an den Aufrufer: sofort anwenden, nebenläufig speichern (App.tsx `aendereEinstellungen`). */
  onSpalten: (n: ProduktSpalten) => void;
  /** Adresswechsel innerhalb des Bereichs; die Adresse kennt App.tsx. */
  onOeffnen: (artikelId: string | null) => void;
};

export type BereichArtikelTeile = {
  /** Für `MeSchale.list` — die Kategorienliste; am Telefon `null` (keine Spalte, die Kategorien stehen im Detail). */
  spalte: ReactElement | null;
  /** Für `MeSchale.detail` — Pfad, Werkzeugzeile und Kachelraster. */
  detail: ReactElement;
};

/** Der Ladezustand des Katalogs: der zuletzt geladene (bleibt beim Neuladen stehen), der Schlüssel der Anfrage, mit
 *  der er geholt wurde (Sicht, Pille, Suche), und ein Fehlertext. */
type Ladung = { katalog: ArtikelKatalog | null; schluessel: string | null; fehler: string | null;
  /** Der Ladestand (`version`), zu dem der Katalog gehört — die Kachel nach dem Speichern sucht erst im neuen. */
  stand: number };

/** Der Ladeschlüssel einer Katalogabfrage: Nur ein Katalog mit dem Schlüssel der aktuellen Filter gilt als „frisch“. */
const ladeSchluessel = (sicht: readonly string[] | undefined, mitPille: boolean, suche: string): string =>
  JSON.stringify([sicht ?? null, mitPille, suche]);

/** Die Kennung der zweiten Pfadstufe (gewählte Kategorie); ein Klick auf jede andere Stufe führt zu „Alle“. */
const KATEGORIE_STUFE = "bart-kategorie";

/** Gehört der Artikel in diese Zeile? „ohne Kategorie“ nimmt die Artikel ohne Kategorie, jede andere ihre Kennungen
 *  (eine Sammelzeile über Unternehmen trägt mehrere, 777). */
const gehoertZu = (a: Artikel, z: KategorieZeile): boolean => (z.key === OHNE_KATEGORIE
  ? a.productCategoryId === null : a.productCategoryId !== null && z.productCategoryIds.includes(a.productCategoryId));

const fehlerText = (fehler: unknown): string =>
  (fehler instanceof ApiError && fehler.status === 413 ? T.tooLarge : T.loadError);

export function useBereichArtikel(p: BereichArtikelProps): BereichArtikelTeile {
  const { client, companies, aktiv, artikelId, istAdmin } = p;
  const telefon = useTelefon();

  const [ladung, setLadung] = useState<Ladung>({ katalog: null, schluessel: null, fehler: null, stand: -1 });
  const [zusatz, setZusatz] = useState<string[]>([]);
  /** Die Sicht (R6): das Arbeitsunternehmen samt den zugeschalteten; `undefined` = nur das aktive. */
  const sicht = useStabileListe(sichtFuer(p.arbeitsunternehmenId, zusatz));
  const [suche, setSuche] = useState("");
  /** Das Feld zeigt jede Eingabe sofort; abgerufen wird erst nach der Verzögerung (Spec „Werkzeugzeile“, wie die
   *  Kundenliste) — drei Tastendrücke sind EIN Katalogabruf. */
  const [sucheEingabe, setSucheEingabe] = useVerzoegerteSuche(suche, (t) => { setSuche(t); filterGewechselt(); });
  const [mitStillgelegten, setMitStillgelegten] = useState(false);
  /** Der Schlüssel der gewählten Kategorienzeile; `null` = „Alle Artikel“. */
  const [kategorie, setKategorie] = useState<string | null>(null);
  const [quittung, setQuittung] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  /** Die Kachel aus der Adresse, sobald sie im Katalog gefunden ist. */
  const [markiert, setMarkiert] = useState<string | null>(null);

  // Zustand für die Dialoge aus Aufgabe 7 (siehe Kopf): gesetzt von Stift, „Neuer Artikel“, Kategorien-Stift und ⋯.
  const [bearbeiten, setBearbeiten] = useState<Artikel | "neu" | null>(null);
  const [pflegeOffen, setPflegeOffen] = useState(false);
  const [menue, setMenue] = useState<{ artikel: Artikel; anker: HTMLElement } | null>(null);
  /** Neu laden mit unveränderten Filtern — nach Speichern, Stilllegen oder Kategorienpflege. */
  const neuLaden = (): void => setVersion((v) => v + 1);
  /** Die geschriebene Kachel, die nach dem Neuladen (Ladestand ab `ab`) in Sicht kommt. */
  const [inSicht, setInSicht] = useState<{ id: string; ab: number } | null>(null);
  const nachSchreiben = (id: string): void => { setInSicht({ id, ab: version + 1 }); neuLaden(); };
  const { bestaetigen } = useBestaetigung();

  /*
   * M6: Ein Filterwechsel durch die Nutzerin lässt die Quittung fallen und räumt die Adresse `/artikel/:id` — sie nennt
   * sonst einen Artikel, der nicht mehr das Thema ist. Die Pille, die sich für RF5 selbst zuschaltet, geht hier nicht
   * durch (sie ist keine Wahl).
   */
  const onOeffnen = p.onOeffnen;
  const filterGewechselt = (): void => {
    setQuittung(null);
    if (artikelId !== null) onOeffnen(null);
  };

  /*
   * Beim VERLASSEN des Bereichs fällt die Filterleiste weg (Suche, Unternehmen, Pille, Kategorie) — ein
   * Bearbeitungszustand ohne eigene Adresse, wie in den Schwesterbereichen. Der geladene Katalog bleibt: Wer
   * zurückkommt, sieht sofort die alte Menge, bis die neue da ist.
   */
  useEffect(() => {
    if (aktiv) return;
    setSuche(""); setZusatz([]); setMitStillgelegten(false); setKategorie(null); setQuittung(null);
    setBearbeiten(null); setPflegeOffen(false); setMenue(null);
  }, [aktiv]);

  const getProductCatalog = client.getProductCatalog;
  useEffect(() => {
    if (!aktiv) return undefined;
    let abgebrochen = false;
    // Nur gesetzte Filter gehen mit (die Vorgaben stehen im Server: aktives Unternehmen, ohne Stillgelegte).
    getProductCatalog({
      ...(sicht === undefined ? {} : { companyIds: sicht }),
      ...(mitStillgelegten ? { includeInactive: true } : {}),
      ...(suche === "" ? {} : { suche }),
    })
      .then((katalog) => {
        if (!abgebrochen) {
          setLadung({ katalog, schluessel: ladeSchluessel(sicht, mitStillgelegten, suche), fehler: null, stand: version });
        }
      })
      .catch((fehler: unknown) => { if (!abgebrochen) setLadung((l) => ({ ...l, fehler: fehlerText(fehler) })); });
    return () => { abgebrochen = true; };
  }, [aktiv, getProductCatalog, sicht, mitStillgelegten, suche, version]);

  const katalog = ladung.fehler === null ? ladung.katalog : null;
  const zeilen = useMemo(() => (katalog === null ? null : sichtbareZeilen(katalog.categories, kategorie)),
    [katalog, kategorie]);

  /* Eine gewählte Kategorie, die es nach dem Neuladen nicht mehr gibt (gelöscht, Unternehmen abgeschaltet): zurück auf
     „Alle“ mit Quittung — die Auswahl springt nie still um. „ohne Kategorie“ fällt nie heraus (`sichtbareZeilen`
     hält die gewählte, I-1). */
  useEffect(() => {
    if (zeilen === null || kategorie === null || zeilen.some((z) => z.key === kategorie)) return;
    setKategorie(null);
    setQuittung(T.categoryGone);
  }, [zeilen, kategorie]);

  /*
   * Die Adresse `/artikel/:id` auflösen — einmal je Kennung (`aufgeloest`) und nur gegen einen FRISCHEN Katalog (sein
   * Ladeschlüssel passt zu Sicht, Pille und Suche): Ein Katalog, der noch zu alten Filtern gehört, sagt über den Artikel
   * nichts. Fehlt er im frischen Katalog ohne Pille, schaltet sie sich zu (`pilleSelbst`) und der Hook wartet auf den
   * Katalog MIT Pille; erst dort ist „nicht gefunden“ endgültig, und die selbst zugeschaltete Pille geht wieder aus.
   */
  const aktuellerSchluessel = ladeSchluessel(sicht, mitStillgelegten, suche);
  const aufgeloest = useRef<string | null>(null);
  const pilleSelbst = useRef(false);
  useEffect(() => {
    if (artikelId === null) { aufgeloest.current = null; setMarkiert(null); return; }
    if (katalog === null || aufgeloest.current === artikelId || ladung.schluessel !== aktuellerSchluessel) return;
    const treffer = katalog.items.find((a) => a.id === artikelId);
    if (treffer === undefined && !mitStillgelegten) {
      pilleSelbst.current = true;
      setMitStillgelegten(true);
      return;
    }
    aufgeloest.current = artikelId;
    if (treffer === undefined) {
      setKategorie(null);
      setMarkiert(null);
      setQuittung(T.articleUnknown);
      if (pilleSelbst.current) setMitStillgelegten(false);
    } else {
      setKategorie(katalog.categories.find((z) => gehoertZu(treffer, z))?.key ?? null);
      setMarkiert(treffer.id);
    }
    pilleSelbst.current = false;
  }, [artikelId, katalog, ladung.schluessel, aktuellerSchluessel, mitStillgelegten]);

  // Die markierte Kachel in Sicht holen — nach dem Zeichnen, ohne die übrige Scrollposition anzufassen.
  const rasterRef = useRef<HTMLDivElement | null>(null);
  const holeInSicht = (id: string): void => {
    const el = rasterRef.current?.querySelector<HTMLElement>(`[data-artikel-id="${id}"]`);
    if (el !== null && el !== undefined && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  };
  useLayoutEffect(() => { if (markiert !== null) holeInSicht(markiert); }, [markiert]);
  /*
   * Nach dem Schreiben: erst im NEU geladenen Katalog (die neue Kachel gibt es im alten nicht). Liegt der Artikel
   * außerhalb der gewählten Kategorie (umgehängt, neu in einer anderen), wechselt die Auswahl zu seiner Zeile — bzw.
   * „Alle“, falls die Zeile fehlt — und die Kachel kommt im nächsten Zeichnen in Sicht (M6). Fehlt er im Katalog ganz
   * (Suche, Pille), bleibt die Auswahl.
   */
  useLayoutEffect(() => {
    if (inSicht === null || ladung.stand < inSicht.ab || katalog === null || zeilen === null) return;
    const geschrieben = katalog.items.find((a) => a.id === inSicht.id);
    const auswahl = kategorie === null ? undefined : zeilen.find((z) => z.key === kategorie);
    if (geschrieben !== undefined && auswahl !== undefined && !gehoertZu(geschrieben, auswahl)) {
      setKategorie(zeilen.find((z) => gehoertZu(geschrieben, z))?.key ?? null);
      return;
    }
    holeInSicht(inSicht.id);
    setInSicht(null);
  }, [inSicht, ladung.stand, katalog, zeilen, kategorie]);

  const waehle = (key: string | null): void => { setKategorie(key); filterGewechselt(); };
  const firmaVon = (id: string): Unternehmen | undefined => companies.find((c) => c.id === id);
  const arbeitsfirma = p.arbeitsunternehmenId === null ? undefined : firmaVon(p.arbeitsunternehmenId);
  const neu = (): void => { if (arbeitsfirma !== undefined) setBearbeiten("neu"); };
  const gewaehlteZeile = kategorie === null ? undefined : zeilen?.find((z) => z.key === kategorie);
  /* Die vorzubelegende Kategorie (P17, P4): Gehört die gewählte Zeile auch dem Arbeitsunternehmen, ist ihr Schlüssel
     dessen Kategorie; eine Zeile nur fremder Unternehmen und „ohne Kategorie“ belegen nichts vor. */
  const vorbelegteKategorieId = gewaehlteZeile !== undefined && gewaehlteZeile.key !== OHNE_KATEGORIE
    && p.arbeitsunternehmenId !== null && gewaehlteZeile.companyIds.includes(p.arbeitsunternehmenId)
    ? gewaehlteZeile.key : null;

  /** ⋯ › Stilllegen bzw. Wieder aktivieren (788): gestapelt gefragt, mit Version geschrieben; 409 → Meldung und neu
   *  laden (RF4). */
  async function aktivSetzen(a: Artikel, aktiv: boolean): Promise<void> {
    const ja = await bestaetigen(aktiv
      ? { titel: DT.confirmActivate(a.productNumber), text: DT.activateConsequence, bestaetigenText: DT.activate }
      : { titel: DT.confirmDeactivate(a.productNumber), text: DT.deactivateConsequence, bestaetigenText: DT.deactivate });
    if (!ja) return;
    try {
      await client.setProductActive(a.id, a.version, aktiv);
      setQuittung(null);
      nachSchreiben(a.id);
    } catch (fehler) {
      setQuittung(fehler instanceof ApiError && fehler.status === 409 ? DT.staleReloaded : DT.actionFailed);
      neuLaden();
    }
  }
  /* Das Menü des ⋯ steht am Knopf der Kachel; geöffnet wird es nach dem Zeichnen am Anker. */
  const menueRef = useRef<MenueElement | null>(null);
  useLayoutEffect(() => { if (menue !== null) menueRef.current?.oeffnen(menue.anker); }, [menue]);

  const kachelnVon = (liste: readonly Artikel[]): ReactElement => (
    <div className="bart__kacheln">
      {liste.map((a) => (
        <ArtikelKachel key={a.id} artikel={a} unternehmen={firmaVon(a.companyId)} istAdmin={istAdmin}
          markiert={a.id === markiert} onBearbeiten={() => setBearbeiten(a)}
          onMehr={(anker) => setMenue({ artikel: a, anker })} />
      ))}
    </div>
  );

  /** Der Inhalt des Rasters: Meldung oder Gruppen in Serverfolge (bei „Alle“ ohne leere Gruppen). */
  function rasterInhalt(): ReactElement {
    if (ladung.fehler !== null) return <p className="bart__meldung bart__meldung--fehler" role="alert">{ladung.fehler}</p>;
    if (katalog === null || zeilen === null) return <p className="bart__meldung" role="status">{T.loading}</p>;
    if (katalog.items.length === 0 && suche === "" && kategorie === null) {
      return (
        <div className="bart__leer">
          <p>{T.empty}</p>
          {istAdmin && <Knopf art="primaer" kompakt onClick={neu}>{T.newArticle}</Knopf>}
        </div>
      );
    }
    const gruppen = (gewaehlteZeile === undefined ? zeilen : [gewaehlteZeile])
      .map((z) => ({ zeile: z, artikel: katalog.items.filter((a) => gehoertZu(a, z)) }))
      .filter((g) => g.artikel.length > 0);
    if (gruppen.length === 0) {
      return <p className="bart__leer">{suche !== "" ? searchEmpty(suche) : T.categoryEmpty}</p>;
    }
    return (
      <>
        {gruppen.map((g) => {
          const name = kategorieName(g.zeile);
          return (
            <section key={g.zeile.key} className="bart__gruppe" aria-label={groupHead(name, g.artikel.length)}>
              <div className="bart__gruppe-kopf">
                <h2 className="bart__gruppe-titel">{name}</h2>
                <span className="bart__gruppe-anzahl" aria-hidden="true">{articleCount(g.artikel.length)}</span>
              </div>
              {kachelnVon(g.artikel)}
            </section>
          );
        })}
      </>
    );
  }

  /* Am Telefon steht der Stift der Kategorienpflege neben den Pillen — die Spalte mit dem Kopf fehlt dort (M5). */
  const pflegeStift = istAdmin && arbeitsfirma !== undefined && (
    <Knopf className="bart__kat-pflege" art="leise" kompakt aria-label={T.editCategories}
      onClick={() => setPflegeOffen(true)}>
      <Icon name="pen" size={14} />
    </Knopf>
  );
  const kategorienPillen = telefon && zeilen !== null && (
    <div className="bart__kat-pillen">
      <Pillenreihe rollend aria-label={T.categories}>
        <Knopf art="sekundaer" gestalt="pille" gedrueckt={kategorie === null} onClick={() => waehle(null)}>
          {T.categoryPill(T.all, summe(zeilen).count)}
        </Knopf>
        {zeilen.map((z) => (
          <Knopf key={z.key} art="sekundaer" gestalt="pille" gedrueckt={kategorie === z.key} onClick={() => waehle(z.key)}>
            {T.categoryPill(kategorieName(z), z.count)}
          </Knopf>
        ))}
      </Pillenreihe>
      {pflegeStift}
    </div>
  );

  const spalte = telefon ? null : (
    <KategorienListe zeilen={zeilen} fehler={ladung.fehler} gewaehlt={kategorie} istAdmin={istAdmin && arbeitsfirma !== undefined}
      onWahl={waehle} onPflege={() => setPflegeOffen(true)} />
  );

  /* Die Zahl hinter „Artikel“ ist die `kennung` der ersten Stufe (me-design 8125d32 hat den Setter dafür). */
  const anzahl = katalog === null ? null : String(katalog.total);

  /* `--n` ist eine Anzahl, kein Maß: Das Raster rechnet die Spaltenbreite aus ihr und `--kachel-min` (CSS). */
  const stil = { "--n": String(p.spalten) } as CSSProperties;
  const detail = (
    <div className="bart" style={stil}>
      <Pfad className="bart__pfad"
        onStufeGewaehlt={(d) => { if (d.id !== KATEGORIE_STUFE) waehle(null); }}
        {...(istAdmin && arbeitsfirma !== undefined ? { rechts: (
          <Knopf art="primaer" kompakt aria-label={T.newArticle} onClick={neu}>
            <Icon name="plus" size={14} />{!telefon && <span className="bart__neu-text">{T.newArticle}</span>}
          </Knopf>
        ) } : {})}>
        <Stufe {...(anzahl === null ? {} : { kennung: anzahl })} beschriftung={T.area} aktiv={gewaehlteZeile === undefined} />
        {gewaehlteZeile !== undefined && <Stufe id={KATEGORIE_STUFE} beschriftung={kategorieName(gewaehlteZeile)} aktiv />}
      </Pfad>
      {kategorienPillen}
      <div className="bart__werkzeuge" role="search">
        <Suchfeld className="bart__suche" name={T.searchLabel} platzhalter={T.searchPlaceholder} wert={sucheEingabe}
          onText={setSucheEingabe} />
        <UnternehmensChips label={T.companiesLabel} companies={companies} aktivId={p.arbeitsunternehmenId}
          zusatz={zusatz} onZusatz={(ids) => { setZusatz(ids); filterGewechselt(); }} />
        <Pillenreihe aria-label={T.stateLabel}>
          <Knopf art="sekundaer" gestalt="pille" gedrueckt={mitStillgelegten}
            onClick={() => { pilleSelbst.current = false; setMitStillgelegten((an) => !an); filterGewechselt(); }}>
            {T.inactivePill(katalog?.inactiveCount ?? 0)}
          </Knopf>
        </Pillenreihe>
        {!telefon && <Spaltenwahl wert={p.spalten} onWahl={p.onSpalten} />}
      </div>
      {quittung !== null && <p className="bart__quittung" role="status">{quittung}</p>}
      <div ref={rasterRef} className={`bart__raster${telefon ? " bart__raster--telefon" : ""}`}>
        {rasterInhalt()}
      </div>
      {dialoge()}
    </div>
  );

  /** Artikel-Dialog, Kategorienpflege und das Menü des ⋯ — nur für ADMIN zu öffnen (789). */
  function dialoge(): ReactElement {
    const unternehmen = bearbeiten === "neu" ? arbeitsfirma : bearbeiten === null ? undefined : firmaVon(bearbeiten.companyId);
    return (
      <>
        {bearbeiten !== null && unternehmen !== undefined && (
          <ArtikelDialog key={bearbeiten === "neu" ? "neu" : bearbeiten.id} client={client} unternehmen={unternehmen}
            artikel={bearbeiten === "neu" ? null : bearbeiten} vorbelegteKategorieId={vorbelegteKategorieId}
            fremdesUnternehmen={unternehmen.id !== p.arbeitsunternehmenId}
            onGespeichert={(a) => { setBearbeiten(null); setQuittung(null); nachSchreiben(a.id); }}
            onSchliessen={(q) => { setBearbeiten(null); setQuittung(q ?? null); }} />
        )}
        {pflegeOffen && arbeitsfirma !== undefined && (
          <KategorienPflege client={client} unternehmen={arbeitsfirma}
            onFertig={(geaendert) => { setPflegeOffen(false); if (geaendert) neuLaden(); }} />
        )}
        {menue !== null && (
          <Menue ref={menueRef} key={menue.artikel.id} onGeschlossen={() => setMenue(null)}
            onGewaehlt={(d) => { const a = menue.artikel; setMenue(null); void aktivSetzen(a, d.wert === "an"); }}>
            {menue.artikel.active
              ? <option value="aus" data-art="gefahr">{DT.deactivate}</option>
              : <option value="an">{DT.activate}</option>}
          </Menue>
        )}
      </>
    );
  }

  return { spalte, detail };
}
